import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Spin, Tag, Input, Divider } from 'antd'
import SearchInput from 'components/SearchInput'
import { FilterText } from 'components/FilterDiv'
import Pagination from 'components/Pagination'
import AntdAvatar from 'components/Avatar'
import Flex from 'components/Flex'
import { TableTop } from 'components/UIComponents/TableSearchFilter/Style/style'
import {
  FlexWrap,
  RowNumberData,
} from 'components/TableStyles/TableContentStyles/TableContentStyles'
import { StyledTableSqare } from 'components/TableStyles/TableStylesSqare/TableStylesSqare'
import {
  EmptyElement,
  EmptySpan,
} from 'components/UIComponents/CommonStyledDiv'
import EmptyCandidateIcon from 'components/icons/EmptyCandidateIcon'
import {
  EmptyWrapper,
} from 'modules/Drives/Components/Styles/style'
import { getInviteCandidates } from 'modules/Exp-Candidates/actions'
import moment from 'moment'
import styled from 'styled-components'

const { Search } = Input

const StatusTag = styled(Tag)`
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border: none;
`

const SearchWrapper = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
`

const CountText = styled.span`
  font-size: 13px;
  color: #888;
  margin-left: auto;
`

const StyledSearchInput = styled(SearchInput)`
  border-right: ${({ hasBorderRight }) =>
    hasBorderRight ? '1px solid #e8e8e8' : 'none'};

  .ant-form-item-control-input {
  }
`

const StyledDivider = styled(Divider)`
  margin: 0 10px;
  border-color: rgba(223, 223, 223, 1);
  height: 50px;
  align-self: center;
`

const getStatusColor = status => {
  switch (status) {
    case 'SENT':
      return { color: '#1565c0', background: '#e3f2fd' }
    case 'APPLIED':
      return { color: '#2e7d32', background: '#e8f5e9' }
    case 'FAILED':
      return { color: '#c62828', background: '#fce4ec' }
    default:
      return { color: '#555', background: '#f0f0f0' }
  }
}

const getInitials = (firstName, lastName) => {
  return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase()
}

const InvitedCandidatesTable = ({ roleId }) => {
  const dispatch = useDispatch()
  const [data, setData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [inputValue, setInputValue] = useState('')
  const debounceTimeoutRef = useRef(null)
  const pageLimit = 10
  const currentCount = data.length

  useEffect(() => {
    if (!roleId) return
    let mounted = true
    setIsLoading(true)
    dispatch(
      getInviteCandidates(roleId, {
        page: currentPage,
        limit: pageLimit,
        search: searchValue || undefined,
      })
    ).then(response => {
      if (!mounted) return
      if (response?.status === 200) {
        const resData = response?.data?.data
        setData(resData?.result || [])
        setTotalCount(resData?.count || 0)
      }
      setIsLoading(false)
    }).catch(() => {
      if (mounted) setIsLoading(false)
    })
    return () => { mounted = false }
  }, [roleId, currentPage, searchValue])

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchValue(inputValue)
      setCurrentPage(0)
    }, 500)
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [inputValue])

  const handlePageChange = page => {
    setCurrentPage(page)
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_, __, index) => (
        <RowNumberData>{currentPage * pageLimit + index + 1}</RowNumberData>
      ),
    },
    {
      title: 'Candidate Name',
      dataIndex: 'firstName',
      key: 'name',
      width: 220,
      render: (_, record) => (
        <span>
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: text => text || '-',
    },
    {
      title: 'Invite Status',
      dataIndex: 'inviteStatus',
      key: 'inviteStatus',
      width: 130,
      render: status => {
        const colors = getStatusColor(status)
        return (
          <StatusTag style={{ color: colors.color, backgroundColor: colors.background }}>
            {status}
          </StatusTag>
        )
      },
    },
    {
      title: 'Invited On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: date => (date ? moment(date).format('DD MMM YYYY') : '-'),
    },
  ]

  return (
    <>
      <TableTop centerVertically>
        <StyledSearchInput
          width={'80%'}
          placeholder={"Search by Keyword"}
          bordered={false}
          searchValue={inputValue}
          setSearchValue={setInputValue}
        />
        <StyledDivider type="vertical" />
        <FilterText>
          Showing {currentCount} of {totalCount}
        </FilterText>
      </TableTop>

      <div>
        <StyledTableSqare
          columns={columns}
          dataSource={data}
          pagination={false}
          rowKey="id"
          scroll={{ x: 940 }}
          loading={isLoading}
        />

        <Pagination
          onChange={pagNum => {
            setCurrentPage(pagNum - 1)
          }}
          totalPages={totalCount}
          pageSize={pageLimit}
          current={currentPage + 1}
        />
      </div>
    </>
  )
}

export default InvitedCandidatesTable
